/**
 * PublicArtifactRowCard
 *
 * Shared clickable card shell for the public artifact rows (task / note /
 * obstruction). Owns the keyboard activation (Enter / Space), focus ring, and
 * a11y wiring so each per-kind row only renders its own body. Fully prop-driven
 * — no store / navigation hooks — so it works on the unauthenticated share /
 * embed pages.
 *
 * @module components/share/public/PublicArtifactList/parts/PublicArtifactRowCard
 */
import { memo, useCallback, type KeyboardEvent, type ReactNode } from 'react';
import { Card } from '@mantine/core';

import styles from './PublicArtifactRows.module.css';

interface PublicArtifactRowCardProps {
  ariaLabel: string;
  onClick: () => void;
  children: ReactNode;
}

export const PublicArtifactRowCard = memo(function PublicArtifactRowCard({
  ariaLabel,
  onClick,
  children,
}: PublicArtifactRowCardProps) {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onClick();
      }
    },
    [onClick]
  );

  return (
    <Card
      padding="sm"
      withBorder
      className={styles.card}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={ariaLabel}
    >
      {children}
    </Card>
  );
});
